import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { API, setToken, clearB2CToken } from "@/lib/api";
import { ShieldAlert, LogOut } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const KEY = "findo_impersonation";

export function readImpersonation() {
  try { return JSON.parse(sessionStorage.getItem(KEY)); } catch { return null; }
}
export function writeImpersonation(obj) {
  sessionStorage.setItem(KEY, JSON.stringify(obj));
  window.dispatchEvent(new Event("findloop:impersonation-changed"));
}
export function clearImpersonation() {
  sessionStorage.removeItem(KEY);
  window.dispatchEvent(new Event("findloop:impersonation-changed"));
}

export default function ImpersonationBanner() {
  const { t } = useLang();
  const [imp, setImp] = useState(readImpersonation());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const sync = () => setImp(readImpersonation());
    window.addEventListener("findloop:impersonation-changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("findloop:impersonation-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  // Auto-expire.
  useEffect(() => {
    if (!imp?.expires_at) return;
    const ms = new Date(imp.expires_at).getTime() - Date.now();
    if (ms <= 0) { quit(true); return; }
    const t = setTimeout(() => quit(true), ms);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [imp?.expires_at]);

  async function quit(expired = false) {
    if (busy) return;
    setBusy(true);
    try {
      if (imp?.session_id && imp?.admin_token) {
        await fetch(`${API}/admin/impersonation/${imp.session_id}/end`, {
          method: "POST",
          headers: { Authorization: `Bearer ${imp.admin_token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ reason: expired ? "expired" : "manual" }),
        }).catch(() => {});
      }
      // Restore admin context.
      if (imp?.target_type === "partner" && imp?.admin_token) {
        setToken(imp.admin_token);
      } else if (imp?.target_type === "b2c") {
        clearB2CToken();
      }
      clearImpersonation();
      toast[expired ? "info" : "success"](expired ? t("hc.impersonationSessionExpired") : t("hc.impersonationEnded"));
      window.location.href = "/superadmin?tab=impersonation";
    } finally { setBusy(false); }
  }

  if (!imp) return null;
  return (
    <div className="fixed top-0 inset-x-0 z-[60] bg-findo-red text-white" data-testid="impersonation-banner">
      <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldAlert size={16} className="flex-shrink-0" />
          <span className="truncate font-semibold">
            {t("hc.impersonationMode")} — {imp.target_type === "partner" ? t("hc.partner2") : t("hc.b2cCustomer")} : {imp.target_name}
          </span>
        </div>
        <button onClick={() => quit(false)} disabled={busy}
          className="flex items-center gap-1.5 rounded-full bg-white/20 hover:bg-white/30 px-3 py-1 font-semibold transition-colors"
          data-testid="impersonation-quit-btn">
          <LogOut size={14} /> {t("hc.exit")}
        </button>
      </div>
    </div>
  );
}
